import React, { useState, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import styles from './UpdateSiteModal.module.css'
import DomainInputForm from './components/DomainInputForm/DomainInputForm'
import SelectedFile from './components/SelectedFile/SelectedFile'
import AccessSettings from './components/AccessSettings/AccessSettings'
import EnableCommentsSection from './components/EnableComments/EnableComments'
import LeadCaptureSection, { formatUrl } from './components/LeadCapture/LeadCapture'
import { Formik, Form } from 'formik'
import { Button } from 'react-bootstrap'
import useCheckIsMobileScreen from 'hooks/checkMobileScreen'
import {
  DISPLAY_MODE_STANDARD,
  GATED_MODE_EMAIL_GATE,
  GATED_MODE_PASSWORD,
  GATED_MODE_EMAIL_WHITELIST,
  GATED_MODE_SSO,
  GATED_MODE_STANDARD,
  LABEL_MAP,
  ACTION_UPDATE,
  LEAD_CAPTURE_MODE_NONE,
  LEAD_CAPTURE_MODE_PROMOTIONAL,
  LEAD_CAPTURE_MODE_EMAIL
} from './constants'
import { getDomainSuffix, isPDF, onUpgradePlan } from 'utils/general'
import { PRO_MAX_FILE_SIZES, PLANS } from 'constants/plans'
import { getAllFileTypes } from 'helpers/file-type'
import { showUpgradeCardModal } from 'actions'
import { setDraggedFile } from 'Manage/actions'
import { uploadedFileTooBig } from 'utils/sendy'
import { MAX_FILE_SIZE, MAX_PDF_SIZE } from '../../constants'
import SEOSettingsSection from './components/AdvancedSettings/SEOSettingsSection'
// import WidgetsSection from './components/Widget/Widget'
import CustomTooltip from '../CustomTooltip'

const SECTIONS = [
  { section: 'main', label: 'General' },
  { section: 'access_settings', label: 'Access' },
  { section: 'enable_comments', label: 'Comments' },
  { section: 'lead_capture', label: 'Lead Capture' },
  { section: 'advanced_settings', label: 'Advanced' }
]

const Section = ({ id, title, icon, children }) => {
  return (
    <div id={id} className={styles.section}>
      {title && (
        <span className={styles.sectionTitle}>
          {icon}
          <h3 className="mb-0 font-18">{title}</h3>
        </span>
      )}
      {children}
    </div>
  )
}

const MenuIcon = ({ type }) => {
  switch (type) {
    case 'main':
      return <img src="/assets/icons/globe-alt.svg" alt="main" width="24" height="24" />
    case 'access_settings':
      return <img src="/assets/icons/eye-filled.svg" alt="access_settings" width="24" height="24" />
    case 'enable_comments':
      return <img src="/assets/icons/feedback-comment.svg" alt="enable_comments" width="24" height="24" />
    case 'advanced_settings':
      return <img src="/assets/icons/settings-filled.svg" alt="advanced_settings" height="24" width="24" />
    case 'lead_capture':
      return <img src="/assets/icons/funnel-filled.svg" alt="lead_capture" height="24" width="24" />
    case 'widgets':
      return <img src="/assets/icons/widget-menu.svg" alt="widgets" height="24" width="24" />
    default:
      return null
  }
}

// Main Modal Component
const UpdateSiteModal = ({ onClose, formValues, onSubmit, validate }) => {
  const { isMobile } = useCheckIsMobileScreen()
  const {
    action,
    userProfile,
    site,
    files = [],
    customDomains,
    tlds,
    disableProFeatures,
    draggedFile,
    currentFormValues
  } = formValues || {}
  const { productId } = userProfile || {}
  const {
    subdomain = '',
    passwordProtected = false,
    password = '',
    fileUploadLimit = PRO_MAX_FILE_SIZES[productId],
    disableIndexing = false,
    disableExportPdf = false,
    siteConfiguration = false,
    displayMode = DISPLAY_MODE_STANDARD,
    enableEmailGate = false,
    feedbackMode = false,
    filename = '' /** v1 */,
    inPreviewMode,
    isGithub = false,
    ogImage = '',
    pageTitle = '',
    metaDescription = '',
    logoImage = '',
    activatedLeadCapture = false,
    leadCaptureTitle = '',
    leadCaptureDescription = '',
    leadCaptureCtaLink = '',
    leadCaptureCtaText = '',
    leadCaptureMode = '',
    enableEmailWhitelist = false,
    allowedEmails = [],
    enableSSO = false
  } = site || {}

  const dispatch = useDispatch()
  const contentRef = useRef(null)
  const [activeTab, setActiveTab] = useState('main')
  const [domainError, setDomainError] = useState(null)

  const queryParams = new URLSearchParams(location.search)
  const urlTab = queryParams.get('tab')

  const scrollToSection = (sectionId) => {
    setActiveTab(sectionId)
    requestAnimationFrame(() => {
      const sectionElement = document.getElementById(sectionId)
      if (sectionElement && contentRef.current) {
        contentRef.current.scrollTo({
          top: sectionElement.offsetTop - 20,
          behavior: 'smooth'
        })
      }
    })
  }

  // Update active tab based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return

      const scrollPosition = contentRef.current.scrollTop

      const mainSection = document.getElementById('main')
      const accessSettingsSection = document.getElementById('access_settings')
      const enableCommentsSection = document.getElementById('enable_comments')
      const advancedSettingsSection = document.getElementById('advanced_settings')
      const leadCaptureSection = document.getElementById('lead_capture')

      if (!mainSection || !accessSettingsSection || !advancedSettingsSection || !leadCaptureSection) return

      // Add buffer to make the detection smoother
      const buffer = 50

      if (scrollPosition + buffer < accessSettingsSection.offsetTop) {
        setActiveTab('main')
      } else if (enableCommentsSection && scrollPosition + buffer < enableCommentsSection.offsetTop) {
        setActiveTab('access_settings')
      } else if (scrollPosition + buffer < leadCaptureSection.offsetTop) {
        setActiveTab('enable_comments')
      } else if (scrollPosition + buffer < advancedSettingsSection.offsetTop) {
        setActiveTab('lead_capture')
      } else {
        setActiveTab('advanced_settings')
      }
    }

    const contentElement = contentRef.current
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  useEffect(() => {
    const tabToScroll = urlTab || 'main'
    scrollToSection(tabToScroll)
  }, [urlTab])

  const updatingPhpFile = site?.type?.includes('php') && action === ACTION_UPDATE

  const domainFormValues = {
    domain: subdomain,
    subdomain: subdomain?.split('.')[0] || '',
    domainSuffix: getDomainSuffix(subdomain).tld || tlds[0],
    customDomains: customDomains,
    tlds
  }

  const uploadFormValues = {
    selectedFile: files?.[0],
    draggedFile: draggedFile,
    addedFiles: files?.length >= 1 ? files.slice(1) : [],
    fileUploadLimit,
    progress: 0,
    indexFile:
      files.find((file) => file.indexFile)?.indexFile ||
      files.find((file) => file.isIndex === true || file.name === 'index.html')?.name ||
      '',
    fileTypes: getAllFileTypes(files || draggedFile) || [],
    isZipPhpFile: false,
    isGithub: isGithub
  }

  const toggleFormValues = {
    displayMode: displayMode,
    feedbackMode: feedbackMode,
    gatedMode: enableSSO
      ? GATED_MODE_SSO
      : enableEmailWhitelist
        ? GATED_MODE_EMAIL_WHITELIST
        : enableEmailGate
          ? GATED_MODE_EMAIL_GATE
          : passwordProtected
            ? GATED_MODE_PASSWORD
            : GATED_MODE_STANDARD,
    enableEmailWhitelist,
    allowedEmails,
    passwordProtected,
    password: enableEmailGate || enableEmailWhitelist ? '' : password,
    disableIndexing: disableIndexing === 'true',
    disableExportPdf,
    siteConfiguration,
    errorPath: siteConfiguration ? JSON.parse(siteConfiguration).errorPath : '/',
    ogImage: ogImage,
    pageTitle: pageTitle,
    metaDescription: metaDescription,
    logoImage: logoImage,
    activatedLeadCapture: activatedLeadCapture,
    leadCaptureTitle: leadCaptureTitle,
    leadCaptureDescription: leadCaptureDescription,
    leadCaptureCtaLink: leadCaptureCtaLink,
    leadCaptureCtaText: leadCaptureCtaText,
    leadCaptureMode: leadCaptureMode || (activatedLeadCapture ? LEAD_CAPTURE_MODE_PROMOTIONAL : LEAD_CAPTURE_MODE_NONE)
  }

  const initialValues = {
    domainValues: domainFormValues,
    uploadValues: uploadFormValues,
    toggleValues: toggleFormValues
  }

  const validateForm = (values) => {
    const errors = validate(values)
    if (domainError) {
      errors.domainValues = errors.domainValues
      errors.domainValues.subdomain = domainError
    }

    if (
      values.toggleValues.ogImage &&
      typeof values.toggleValues.ogImage !== 'string' &&
      values.toggleValues.ogImage.size > 300 * 1024
    ) {
      errors.toggleValues.ogImage = 'Your file is above the 300KB limit'
    }

    if (
      values.toggleValues.logoImage &&
      typeof values.toggleValues.logoImage !== 'string' &&
      values.toggleValues.logoImage.size > 5 * 1024 * 1024
    ) {
      errors.toggleValues.logoImage = 'Your file is above the 5MB limit'
    }

    if (site?.isTransferRequestLink && errors.uploadValues?.selectedFile) {
      delete errors.uploadValues.selectedFile
      if (Object.keys(errors.uploadValues).length === 0) {
        delete errors.uploadValues
      }
    }

    return errors
  }

  const transformValues = (values) => {
    return {
      ...values,
      toggleValues: {
        ...values.toggleValues,
        leadCaptureCtaLink: formatUrl(values.toggleValues.leadCaptureCtaLink)
      }
    }
  }

  return (
    <Formik
      initialValues={currentFormValues || initialValues}
      onSubmit={(values) => onSubmit(transformValues(values), initialValues)}
      validate={validateForm}
      validateOnBlur={true}
      validateOnChange={true}
    >
      {({ values: { domainValues, uploadValues, toggleValues }, setFieldValue, isValid, errors }) => {
        const canSubmit = isValid && (!!uploadValues.selectedFile || site?.isTransferRequestLink)
        return (
          <Form>
            <div className={styles.updateContainer}>
              <div className={styles.updateHeader}>
                <h2>Update Project</h2>
                {!isMobile && (
                  <button className={styles.closeButton} onClick={onClose} aria-label="Close modal" type="button">
                    <img src="/assets/icons/cross.png" alt="close" width="15" height="15" />
                  </button>
                )}
              </div>

              <div className={styles.updateContent}>
                {/* Side Menu */}
                {(!!uploadValues.selectedFile || site?.isTransferRequestLink) && (
                  <div className={styles.sideMenu}>
                    {SECTIONS.map(({ section, label }) => (
                      <button
                        key={section}
                        type="button"
                        className={`${styles.menuItem} ${activeTab === section ? styles.active : ''}`}
                        onClick={() => scrollToSection(section)}
                        aria-label={`Go to ${section} section`}
                      >
                        <CustomTooltip label={label} placement="right">
                          <MenuIcon type={section} />
                        </CustomTooltip>
                      </button>
                    ))}
                  </div>
                )}

                <div className={styles.mainContentWrapper}>
                  {/* Main Content Area */}
                  <div className={styles.mainContent} ref={contentRef}>
                    {/* Main Settings Section */}
                    <Section id="main">
                      <DomainInputForm
                        name={'domainValues'}
                        initialValues={domainValues}
                        userProfile={userProfile}
                        inPreviewMode={inPreviewMode}
                        action={action}
                        disableProFeatures={disableProFeatures}
                        setFieldValue={setFieldValue}
                        uploadValues={uploadValues}
                        updatingPhpFile={updatingPhpFile}
                        setDomainError={setDomainError}
                        domainError={domainError}
                        disabled={feedbackMode}
                      />

                      <SelectedFile
                        draggedFile={draggedFile}
                        productId={userProfile?.productId}
                        setFieldValue={setFieldValue}
                        domainValues={domainValues}
                        uploadValues={uploadValues}
                        isGithub={isGithub}
                        site={site}
                        showUpgradeModal={(file, maxSizeMB, isTotal) => {
                          dispatch(
                            showUpgradeCardModal({
                              title: 'Upgrade to upload larger files',
                              desc: `Your current plan only allows you to upload ${isPDF(file) ? 'PDFs' : 'files'} less than ${
                                isPDF(file) && !productId ? MAX_PDF_SIZE : maxSizeMB || MAX_FILE_SIZE
                              }MB. ${isTotal ? 'Total files should be less than this limit.' : ''} Upload more on a different plan.`,
                              cta: 'Upload larger files',
                              onClick: file?.size ? () => onUpgradePlan({ fileSize: file?.size }, dispatch) : null
                            })
                          )
                          uploadedFileTooBig()
                        }}
                        resetDraggedFile={() => dispatch(setDraggedFile(null))}
                      />
                    </Section>

                    {/* Access Settings Section */}
                    <AccessSettings
                      uploadValues={uploadValues}
                      toggleValues={toggleValues}
                      setFieldValue={setFieldValue}
                      name="toggleValues"
                      productId={productId}
                      sectionIcon={<img src="/assets/icons/eye-filled.svg" alt="settings" height="24" width="24" />}
                      site={site}
                    />

                    {/* Enable Comments Section */}
                    <EnableCommentsSection
                      toggleValues={toggleValues}
                      setFieldValue={setFieldValue}
                      uploadValues={uploadValues}
                      site={site}
                    />

                    {/* Lead Capture Section */}
                    <LeadCaptureSection
                      toggleValues={toggleValues}
                      setFieldValue={setFieldValue}
                      userProfile={userProfile}
                      uploadValues={uploadValues}
                      showUpgradeModal={() => {
                        dispatch(
                          showUpgradeCardModal({
                            title: 'Upgrade to add a promotional slide-in',
                            desc: `Convert more visitors with compelling calls-to-action that drive engagement.`,
                            cta: 'Upgrade now',
                            gif: '/assets/img/gifs/slide-in.gif',
                            onClick: () => onUpgradePlan({ planId: PLANS.PRO.id }, dispatch)
                          })
                        )
                        uploadedFileTooBig()
                      }}
                      errors={errors}
                      site={site}
                    />

                    {/* SEO Settings Section */}
                    <SEOSettingsSection
                      setFieldValue={setFieldValue}
                      userProfile={userProfile}
                      toggleValues={toggleValues}
                      uploadValues={uploadValues}
                      siteType={site?.siteType || site?.linkRecord?.siteType}
                      site={site}
                    />
                  </div>
                  <div className={styles.modalFooter}>
                    <Button
                      className={styles.publishBtn}
                      aria-label="Publish changes"
                      disabled={!canSubmit}
                      type="submit"
                      data-testid="UpdateSiteModal-button-submit"
                    >
                      Publish
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default UpdateSiteModal
